import { z } from 'zod';
import { I18nStringSchema, MoneySchema, TimestampSchema } from './primitives.js';

/**
 * An atomic service (Exterior Wash, Vacuum, Tire Shine).
 *
 * Sprint 104 — migrated to tenant-library at `tenants/{tid}/services/{id}`
 * (was per-outlet). `excludedOutletIds` carries the per-outlet visibility
 * per the smart-default UX (empty = available everywhere). See
 * [[project-tenant-library-catalog]].
 */
export const ServiceSchema = z.object({
  id: z.string(),
  name: I18nStringSchema,
  description: I18nStringSchema.optional(),
  durationMin: z.number().int().positive(),
  active: z.boolean().default(true),
  excludedOutletIds: z
    .array(z.string())
    .default([])
    .describe('Outlets where this service is HIDDEN. Empty (default) = available everywhere.'),
});
export type Service = z.infer<typeof ServiceSchema>;

/**
 * An optional extra (Air Freshener, Premium Wax).
 *
 * Sprint 104 — migrated to tenant-library at `tenants/{tid}/addOns/{id}`.
 * Same `excludedOutletIds` pattern as ServiceSchema.
 */
export const AddOnSchema = z.object({
  id: z.string(),
  name: I18nStringSchema,
  description: I18nStringSchema.optional(),
  pricingByVehicleType: z.record(z.string(), MoneySchema).describe('vehicleTypeId → price (sen)'),
  durationMin: z.number().int().nonnegative().default(0),
  active: z.boolean().default(true),
  excludedOutletIds: z
    .array(z.string())
    .default([])
    .describe('Outlets where this add-on is HIDDEN. Empty (default) = available everywhere.'),
});
export type AddOn = z.infer<typeof AddOnSchema>;

/**
 * A bundled package (Basic/Standard/Premium/Deluxe/Detail). The customer's
 * primary booking unit.
 *
 * Sprint 105 — migrated to tenant-library at `tenants/{tid}/packages/{id}`
 * (was per-outlet). `excludedOutletIds` carries per-outlet visibility per the
 * smart-default UX (empty = available everywhere). `outletPriceOverrides`
 * lets a specific outlet charge more/less for the same package without
 * forking it — see `effectivePackagePrice()` for the resolution order.
 * See [[project-tenant-library-catalog]].
 */
export const PackageSchema = z.object({
  id: z.string(),
  name: I18nStringSchema,
  description: I18nStringSchema.optional(),
  includedServiceIds: z.array(z.string()).min(1),
  defaultAddOnIds: z.array(z.string()).default([]),
  pricingByVehicleType: z
    .record(z.string(), MoneySchema)
    .describe('vehicleTypeId → default price (sen). Override per outlet via outletPriceOverrides.'),
  outletPriceOverrides: z
    .record(z.string(), z.record(z.string(), MoneySchema))
    .default({})
    .describe(
      'outletId → (vehicleTypeId → price sen). Missing outlet or vehicleType falls back to pricingByVehicleType.',
    ),
  estimatedDurationMin: z.number().int().positive(),
  tierRank: z.number().int().describe('Lower = simpler tier'),
  badge: z.enum(['most_popular', 'best_value', 'premium', 'detail', 'none']).default('none'),
  photoUrl: z.string().url().optional(),
  savingsHint: MoneySchema.optional().describe('Auto-computed vs a-la-carte sum, in sen'),
  active: z.boolean().default(true),
  featured: z.boolean().default(false),
  excludedOutletIds: z
    .array(z.string())
    .default([])
    .describe('Outlets where this package is HIDDEN. Empty (default) = available everywhere.'),
  pricingVersion: z.number().int().nonnegative().default(0),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Package = z.infer<typeof PackageSchema>;
